const DEFAULT_TTL_MS = 15_000;
const MAX_CACHE_ENTRIES = 100;

const responseCache = new Map();
const inFlightRequests = new Map();
let cacheVersion = 0;
const tagVersions = new Map();

const stableSerialize = (value) => {
  if (value == null) return String(value);
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  if (typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => (
      `${JSON.stringify(key)}:${stableSerialize(value[key])}`
    )).join(',')}}`;
  }
  return JSON.stringify(value);
};

export const createRequestCacheKey = ({ generation, token, url, params, responseType }) => (
  [generation, token || 'anonymous', url, stableSerialize(params || {}), responseType || 'json'].join('|')
);

const pruneCache = () => {
  const now = Date.now();
  responseCache.forEach((entry, key) => {
    if (entry.expiresAt <= now) responseCache.delete(key);
  });

  while (responseCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = responseCache.keys().next().value;
    responseCache.delete(oldestKey);
  }
};

/**
 * Shares identical in-flight reads and briefly reuses successful responses.
 * Errors are never cached, so retry actions always reach the network.
 */
export const getCachedRequest = ({
  key,
  request,
  ttl = DEFAULT_TTL_MS,
  forceRefresh = false,
  tags = [],
}) => {
  const now = Date.now();
  const cached = responseCache.get(key);
  if (!forceRefresh && cached && cached.expiresAt > now) {
    // Refresh insertion order so frequently used entries survive pruning.
    responseCache.delete(key);
    responseCache.set(key, cached);
    return Promise.resolve(cached.value);
  }

  if (!forceRefresh && inFlightRequests.has(key)) return inFlightRequests.get(key).promise;
  const versionAtStart = cacheVersion;
  const versionsAtStart = tags.map((tag) => tagVersions.get(tag) || 0);

  let requestResult;
  try {
    requestResult = request();
  } catch (error) {
    requestResult = Promise.reject(error);
  }

  const pending = Promise.resolve(requestResult)
    .then((value) => {
      if (ttl > 0 && inFlightRequests.get(key)?.promise === pending && versionAtStart === cacheVersion && tags.every((tag, i) => (tagVersions.get(tag) || 0) === versionsAtStart[i])) {
        responseCache.set(key, { value, tags, expiresAt: Date.now() + ttl });
        pruneCache();
      }
      return value;
    })
    .finally(() => {
      if (inFlightRequests.get(key)?.promise === pending) inFlightRequests.delete(key);
    });

  inFlightRequests.set(key, { promise: pending, tags });
  return pending;
};

export const clearRequestCache = () => {
  cacheVersion += 1;
  responseCache.clear();
  inFlightRequests.clear();
  tagVersions.clear();
};

export const invalidateRequestTags = (tags) => {
  const affected = new Set(tags);
  affected.forEach((tag) => tagVersions.set(tag, (tagVersions.get(tag) || 0) + 1));
  [responseCache, inFlightRequests].forEach((entries) => entries.forEach((entry, key) => {
    if (entry.tags?.some((tag) => affected.has(tag))) entries.delete(key);
  }));
};

export const REQUEST_CACHE_DEFAULT_TTL_MS = DEFAULT_TTL_MS;
